/**
 * TanStack Query hooks for the finance feature. Keys are namespaced
 * "finance/…" so one invalidation never reaches into another lane's cache.
 *
 * Cursor lists ride `useCursorList` over the `{ items, next_cursor }` keyset
 * envelope. Two finance collections deliberately do not: `listCustomerInvoices`
 * publishes no cursor in the contract (it is a filtered worklist), and the
 * reference lookups are bounded single reads.
 */
import { useQuery } from "@tanstack/react-query";

import { useCursorList, type CursorQueryResult } from "@/features/shared/ops-api";

import {
  getAPAgingReport,
  getARAgingReport,
  getBankStatementLine,
  getCustomerInvoice,
  getCustomerReceipt,
  getDepreciationSchedule,
  getFixedAsset,
  getGrn,
  getPaymentRun,
  getPurchaseOrder,
  getPeriodCloseChecklist,
  getSupplierInvoice,
  getTaxCode,
  getYearEndClose,
  listAssetClasses,
  listBankAccounts,
  listBankImportProfiles,
  listBankMatchSuggestions,
  listBankStatementLines,
  listBankStatements,
  listCustomerInvoices,
  listDepreciationRuns,
  listFiscalPeriods,
  listFiscalYears,
  listFixedAssets,
  listGatewayPaymentExceptions,
  listGatewayPayments,
  listGatewayPayouts,
  listGrns,
  listParties,
  listPostableAccounts,
  listPurchaseOrders,
  listCreditNotes,
  listCustomerReceipts,
  listPaymentRuns,
  listSupplierInvoices,
  listTaxCodes,
  type APAgingReport,
  type AssetClass,
  type BankAccount,
  type BankStatement,
  type DepreciationRun,
  type APAgingReportQuery,
  type ARAgingReport,
  type ARAgingReportQuery,
  type Account,
  type BankAccountListQuery,
  type BankImportProfile,
  type BankLineMatchResult,
  type BankMatchSuggestion,
  type BankStatementLine,
  type BankStatementLineListQuery,
  type BankStatementListQuery,
  type CustomerInvoice,
  type CustomerInvoiceListQuery,
  type CreditNote,
  type CustomerReceipt,
  type DepreciationSchedule,
  type FiscalPeriod,
  type FiscalYear,
  type FixedAsset,
  type FixedAssetListQuery,
  type GatewayExceptionListQuery,
  type GatewayPayment,
  type GatewayPaymentException,
  type GatewayPaymentListQuery,
  type GatewayPayout,
  type GatewayPayoutListQuery,
  type Grn,
  type GrnSummary,
  type PartyRole,
  type PurchaseOrder,
  type PurchaseOrderSummary,
  type PartySummary,
  type PaymentRun,
  type PeriodCloseChecklist,
  type SupplierInvoice,
  type SupplierInvoiceListQuery,
  type TaxCode,
  type YearEndClose,
} from "./api";

/**
 * The generated query-parameter types are deeply `readonly`. A screen builds
 * its filter object field by field, so the filter aliases below drop the
 * modifier while keeping the generated value types (including the enums) —
 * the wire vocabulary still comes from the contract, never from a hand-written
 * union.
 */
type Mutable<T> = { -readonly [K in keyof T]: T[K] };

export const financeKeys = {
  taxCodes: {
    all: ["finance/tax-codes"] as const,
    list: (filters: Record<string, unknown>) => ["finance/tax-codes", "list", filters] as const,
    detail: (id: string) => ["finance/tax-codes", "detail", id] as const,
  },
  supplierInvoices: {
    all: ["finance/supplier-invoices"] as const,
    list: (filters: Record<string, unknown>) =>
      ["finance/supplier-invoices", "list", filters] as const,
    detail: (id: string) => ["finance/supplier-invoices", "detail", id] as const,
  },
  customerInvoices: {
    all: ["finance/customer-invoices"] as const,
    list: (filters: Record<string, unknown>) =>
      ["finance/customer-invoices", "list", filters] as const,
    detail: (id: string) => ["finance/customer-invoices", "detail", id] as const,
  },
  customerReceipts: {
    all: ["finance/customer-receipts"] as const,
    list: (filters: Record<string, unknown>) =>
      ["finance/customer-receipts", "list", filters] as const,
    detail: (id: string) => ["finance/customer-receipts", "detail", id] as const,
  },
  paymentRuns: {
    all: ["finance/payment-runs"] as const,
    list: (filters: Record<string, unknown>) => ["finance/payment-runs", "list", filters] as const,
    detail: (id: string) => ["finance/payment-runs", "detail", id] as const,
  },
  creditNotes: {
    all: ["finance/credit-notes"] as const,
    list: (filters: Record<string, unknown>) => ["finance/credit-notes", "list", filters] as const,
  },
  bankAccounts: {
    all: ["finance/bank-accounts"] as const,
    list: (filters: Record<string, unknown>) => ["finance/bank-accounts", "list", filters] as const,
    profiles: (id: string) => ["finance/bank-accounts", "import-profiles", id] as const,
  },
  bankStatements: {
    all: ["finance/bank-statements"] as const,
    list: (filters: Record<string, unknown>) =>
      ["finance/bank-statements", "list", filters] as const,
  },
  bankStatementLines: {
    all: ["finance/bank-statement-lines"] as const,
    list: (filters: Record<string, unknown>) =>
      ["finance/bank-statement-lines", "list", filters] as const,
    detail: (id: string) => ["finance/bank-statement-lines", "detail", id] as const,
    suggestions: (id: string) => ["finance/bank-statement-lines", "suggestions", id] as const,
  },
  assetClasses: {
    all: ["finance/asset-classes"] as const,
    list: () => ["finance/asset-classes", "list"] as const,
  },
  fixedAssets: {
    all: ["finance/fixed-assets"] as const,
    list: (filters: Record<string, unknown>) => ["finance/fixed-assets", "list", filters] as const,
    detail: (id: string) => ["finance/fixed-assets", "detail", id] as const,
    schedule: (id: string) => ["finance/fixed-assets", "schedule", id] as const,
  },
  depreciationRuns: {
    all: ["finance/depreciation-runs"] as const,
    list: () => ["finance/depreciation-runs", "list"] as const,
  },
  periodClose: {
    all: ["finance/period-close"] as const,
    checklist: (fiscalPeriodId: string) =>
      ["finance/period-close", "checklist", fiscalPeriodId] as const,
    yearEnd: (fiscalYearId: string) => ["finance/period-close", "year-end", fiscalYearId] as const,
  },
  gateway: {
    all: ["finance/gateway"] as const,
    payments: (filters: Record<string, unknown>) =>
      ["finance/gateway", "payments", filters] as const,
    exceptions: (filters: Record<string, unknown>) =>
      ["finance/gateway", "exceptions", filters] as const,
    payouts: (filters: Record<string, unknown>) => ["finance/gateway", "payouts", filters] as const,
  },
  reports: {
    all: ["finance/reports"] as const,
    arAging: (filters: Record<string, unknown>) =>
      ["finance/reports", "ar-aging", filters] as const,
    apAging: (filters: Record<string, unknown>) =>
      ["finance/reports", "ap-aging", filters] as const,
  },
  lookups: {
    all: ["finance/lookups"] as const,
    accounts: () => ["finance/lookups", "accounts"] as const,
    parties: (role: string) => ["finance/lookups", "parties", role] as const,
    fiscalPeriods: () => ["finance/lookups", "fiscal-periods"] as const,
    fiscalYears: () => ["finance/lookups", "fiscal-years"] as const,
    purchaseOrders: (supplierId: string) =>
      ["finance/lookups", "purchase-orders", supplierId] as const,
    purchaseOrder: (purchaseOrderId: string) =>
      ["finance/lookups", "purchase-order", purchaseOrderId] as const,
    grns: (purchaseOrderId: string) => ["finance/lookups", "grns", purchaseOrderId] as const,
    grnLines: (purchaseOrderId: string, grnIds: readonly string[]) =>
      ["finance/lookups", "grn-lines", purchaseOrderId, grnIds] as const,
  },
};

// ── Tax codes ────────────────────────────────────────────────────────────

export function useTaxCodeList(
  filters: TaxCodeListQueryFilters,
  limit?: number,
): CursorQueryResult<TaxCode> {
  return useCursorList(
    financeKeys.taxCodes.list({ ...filters, limit }),
    ({ cursor, limit: pageLimit, signal }) =>
      listTaxCodes(
        {
          ...filters,
          ...(cursor === undefined || cursor === null ? {} : { cursor }),
          ...(pageLimit === undefined ? {} : { limit: pageLimit }),
        },
        signal,
      ),
    { limit },
  );
}

/** Filter subset a screen controls; paging keys come from the cursor args. */
export interface TaxCodeListQueryFilters {
  active?: boolean;
  jurisdiction?: string;
}

export function useTaxCode(taxCodeId: string | undefined) {
  return useQuery<TaxCode>({
    queryKey: financeKeys.taxCodes.detail(taxCodeId ?? ""),
    queryFn: ({ signal }) => getTaxCode(taxCodeId as string, signal),
    enabled: taxCodeId !== undefined,
  });
}

// ── Supplier invoices ────────────────────────────────────────────────────

export type SupplierInvoiceFilters = Mutable<Omit<SupplierInvoiceListQuery, "cursor" | "limit">>;

// The three worklists the contract could not answer until now. Each is a
// keyset page, so the screen never asks for "everything".
export function usePaymentRunList(
  filters: { status?: string; branch_id?: string } = {},
  limit?: number,
): CursorQueryResult<PaymentRun> {
  return useCursorList(
    financeKeys.paymentRuns.list({ ...filters, limit }),
    ({ cursor, limit: pageLimit, signal }) =>
      listPaymentRuns(
        {
          ...filters,
          ...(cursor === undefined || cursor === null ? {} : { cursor }),
          ...(pageLimit === undefined ? {} : { limit: pageLimit }),
        },
        signal,
      ),
    { limit },
  );
}

export function useCustomerReceiptList(
  filters: { party_id?: string; branch_id?: string } = {},
  limit?: number,
): CursorQueryResult<CustomerReceipt> {
  return useCursorList(
    financeKeys.customerReceipts.list({ ...filters, limit }),
    ({ cursor, limit: pageLimit, signal }) =>
      listCustomerReceipts(
        {
          ...filters,
          ...(cursor === undefined || cursor === null ? {} : { cursor }),
          ...(pageLimit === undefined ? {} : { limit: pageLimit }),
        },
        signal,
      ),
    { limit },
  );
}

export function useCreditNoteList(
  filters: { invoice_id?: string; party_id?: string } = {},
  limit?: number,
): CursorQueryResult<CreditNote> {
  return useCursorList(
    financeKeys.creditNotes.list({ ...filters, limit }),
    ({ cursor, limit: pageLimit, signal }) =>
      listCreditNotes(
        {
          ...filters,
          ...(cursor === undefined || cursor === null ? {} : { cursor }),
          ...(pageLimit === undefined ? {} : { limit: pageLimit }),
        },
        signal,
      ),
    { limit },
  );
}

export function useSupplierInvoiceList(
  filters: SupplierInvoiceFilters,
  limit?: number,
): CursorQueryResult<SupplierInvoice> {
  return useCursorList(
    financeKeys.supplierInvoices.list({ ...filters, limit }),
    ({ cursor, limit: pageLimit, signal }) =>
      listSupplierInvoices(
        {
          ...filters,
          ...(cursor === undefined || cursor === null ? {} : { cursor }),
          ...(pageLimit === undefined ? {} : { limit: pageLimit }),
        },
        signal,
      ),
    { limit },
  );
}

export function useSupplierInvoice(supplierInvoiceId: string | undefined) {
  return useQuery<SupplierInvoice>({
    queryKey: financeKeys.supplierInvoices.detail(supplierInvoiceId ?? ""),
    queryFn: ({ signal }) => getSupplierInvoice(supplierInvoiceId as string, signal),
    enabled: supplierInvoiceId !== undefined,
  });
}

// ── Customer invoices, credit notes, receipts ────────────────────────────

export function useCustomerInvoiceList(filters: CustomerInvoiceListQuery, enabled = true) {
  return useQuery<CustomerInvoice[]>({
    queryKey: financeKeys.customerInvoices.list(filters),
    queryFn: ({ signal }) => listCustomerInvoices(filters, signal),
    enabled,
  });
}

export function useCustomerInvoice(invoiceId: string | undefined) {
  return useQuery<CustomerInvoice>({
    queryKey: financeKeys.customerInvoices.detail(invoiceId ?? ""),
    queryFn: ({ signal }) => getCustomerInvoice(invoiceId as string, signal),
    enabled: invoiceId !== undefined,
  });
}

export function useCustomerReceipt(receiptId: string | undefined) {
  return useQuery<CustomerReceipt>({
    queryKey: financeKeys.customerReceipts.detail(receiptId ?? ""),
    queryFn: ({ signal }) => getCustomerReceipt(receiptId as string, signal),
    enabled: receiptId !== undefined,
  });
}

// ── Payment runs ─────────────────────────────────────────────────────────

export function usePaymentRun(paymentRunId: string | undefined) {
  return useQuery<PaymentRun>({
    queryKey: financeKeys.paymentRuns.detail(paymentRunId ?? ""),
    queryFn: ({ signal }) => getPaymentRun(paymentRunId as string, signal),
    enabled: paymentRunId !== undefined,
  });
}

// ── Banking ──────────────────────────────────────────────────────────────

export type BankAccountFilters = Mutable<Omit<BankAccountListQuery, "cursor" | "limit">>;

export function useBankAccountList(
  filters: BankAccountFilters = {},
  limit?: number,
): CursorQueryResult<BankAccount> {
  return useCursorList(
    financeKeys.bankAccounts.list({ ...filters, limit }),
    ({ cursor, limit: pageLimit, signal }) =>
      listBankAccounts(
        {
          ...filters,
          ...(cursor === undefined || cursor === null ? {} : { cursor }),
          ...(pageLimit === undefined ? {} : { limit: pageLimit }),
        },
        signal,
      ),
    { limit },
  );
}

export function useBankImportProfiles(bankAccountId: string | null, enabled = true) {
  return useQuery<BankImportProfile[]>({
    queryKey: financeKeys.bankAccounts.profiles(bankAccountId ?? ""),
    queryFn: ({ signal }) => listBankImportProfiles(bankAccountId as string, signal),
    enabled: bankAccountId !== null && enabled,
  });
}

export type BankStatementFilters = Mutable<Omit<BankStatementListQuery, "cursor" | "limit">>;

export function useBankStatementList(
  filters: BankStatementFilters = {},
  limit?: number,
): CursorQueryResult<BankStatement> {
  return useCursorList(
    financeKeys.bankStatements.list({ ...filters, limit }),
    ({ cursor, limit: pageLimit, signal }) =>
      listBankStatements(
        {
          ...filters,
          ...(cursor === undefined || cursor === null ? {} : { cursor }),
          ...(pageLimit === undefined ? {} : { limit: pageLimit }),
        },
        signal,
      ),
    { limit },
  );
}

export type BankStatementLineFilters = Mutable<
  Omit<BankStatementLineListQuery, "cursor" | "limit">
>;

export function useBankStatementLineList(
  filters: BankStatementLineFilters = {},
  limit?: number,
): CursorQueryResult<BankStatementLine> {
  return useCursorList(
    financeKeys.bankStatementLines.list({ ...filters, limit }),
    ({ cursor, limit: pageLimit, signal }) =>
      listBankStatementLines(
        {
          ...filters,
          ...(cursor === undefined || cursor === null ? {} : { cursor }),
          ...(pageLimit === undefined ? {} : { limit: pageLimit }),
        },
        signal,
      ),
    { limit },
  );
}

export function useBankStatementLine(statementLineId: string | null) {
  return useQuery<BankLineMatchResult>({
    queryKey: financeKeys.bankStatementLines.detail(statementLineId ?? ""),
    queryFn: ({ signal }) => getBankStatementLine(statementLineId as string, signal),
    enabled: statementLineId !== null,
  });
}

export function useBankMatchSuggestions(statementLineId: string | null, enabled = true) {
  return useQuery<BankMatchSuggestion[]>({
    queryKey: financeKeys.bankStatementLines.suggestions(statementLineId ?? ""),
    queryFn: ({ signal }) => listBankMatchSuggestions(statementLineId as string, signal),
    enabled: statementLineId !== null && enabled,
  });
}

// ── Fixed assets ─────────────────────────────────────────────────────────

export function useAssetClasses(enabled = true) {
  return useQuery<AssetClass[]>({
    queryKey: financeKeys.assetClasses.list(),
    queryFn: async ({ signal }) => (await listAssetClasses(signal)).items,
    enabled,
  });
}

export type FixedAssetFilters = Mutable<Omit<FixedAssetListQuery, "cursor" | "limit">>;

export function useFixedAssetList(
  filters: FixedAssetFilters = {},
  limit?: number,
): CursorQueryResult<FixedAsset> {
  return useCursorList(
    financeKeys.fixedAssets.list({ ...filters, limit }),
    ({ cursor, limit: pageLimit, signal }) =>
      listFixedAssets(
        {
          ...filters,
          ...(cursor === undefined || cursor === null ? {} : { cursor }),
          ...(pageLimit === undefined ? {} : { limit: pageLimit }),
        },
        signal,
      ),
    { limit },
  );
}

export function useFixedAsset(fixedAssetId: string | undefined) {
  return useQuery<FixedAsset>({
    queryKey: financeKeys.fixedAssets.detail(fixedAssetId ?? ""),
    queryFn: ({ signal }) => getFixedAsset(fixedAssetId as string, signal),
    enabled: fixedAssetId !== undefined,
  });
}

export function useDepreciationSchedule(fixedAssetId: string | undefined, enabled = true) {
  return useQuery<DepreciationSchedule>({
    queryKey: financeKeys.fixedAssets.schedule(fixedAssetId ?? ""),
    queryFn: ({ signal }) => getDepreciationSchedule(fixedAssetId as string, signal),
    enabled: fixedAssetId !== undefined && enabled,
  });
}

export function useDepreciationRuns(enabled = true) {
  return useQuery<DepreciationRun[]>({
    queryKey: financeKeys.depreciationRuns.list(),
    queryFn: async ({ signal }) => (await listDepreciationRuns(signal)).items,
    enabled,
  });
}

// ── Period close ─────────────────────────────────────────────────────────

export function usePeriodCloseChecklist(fiscalPeriodId: string | null) {
  return useQuery<PeriodCloseChecklist>({
    queryKey: financeKeys.periodClose.checklist(fiscalPeriodId ?? ""),
    queryFn: ({ signal }) => getPeriodCloseChecklist(fiscalPeriodId as string, signal),
    enabled: fiscalPeriodId !== null,
  });
}

/**
 * A year with no close is the normal state, and the contract says so with a
 * 404. Retrying it would be noise, so this read never retries.
 */
export function useYearEndClose(fiscalYearId: string | null, enabled = true) {
  return useQuery<YearEndClose>({
    queryKey: financeKeys.periodClose.yearEnd(fiscalYearId ?? ""),
    queryFn: ({ signal }) => getYearEndClose(fiscalYearId as string, signal),
    enabled: fiscalYearId !== null && enabled,
    retry: false,
  });
}

// ── Gateway ──────────────────────────────────────────────────────────────

export type GatewayPaymentFilters = Mutable<Omit<GatewayPaymentListQuery, "cursor" | "limit">>;
export type GatewayExceptionFilters = Mutable<Omit<GatewayExceptionListQuery, "cursor" | "limit">>;
export type GatewayPayoutFilters = Mutable<Omit<GatewayPayoutListQuery, "cursor" | "limit">>;

export function useGatewayPaymentList(
  filters: GatewayPaymentFilters = {},
  limit?: number,
): CursorQueryResult<GatewayPayment> {
  return useCursorList(
    financeKeys.gateway.payments({ ...filters, limit }),
    ({ cursor, limit: pageLimit, signal }) =>
      listGatewayPayments(
        {
          ...filters,
          ...(cursor === undefined || cursor === null ? {} : { cursor }),
          ...(pageLimit === undefined ? {} : { limit: pageLimit }),
        },
        signal,
      ),
    { limit },
  );
}

export function useGatewayExceptionList(
  filters: GatewayExceptionFilters = {},
  limit?: number,
): CursorQueryResult<GatewayPaymentException> {
  return useCursorList(
    financeKeys.gateway.exceptions({ ...filters, limit }),
    ({ cursor, limit: pageLimit, signal }) =>
      listGatewayPaymentExceptions(
        {
          ...filters,
          ...(cursor === undefined || cursor === null ? {} : { cursor }),
          ...(pageLimit === undefined ? {} : { limit: pageLimit }),
        },
        signal,
      ),
    { limit },
  );
}

export function useGatewayPayoutList(
  filters: GatewayPayoutFilters = {},
  limit?: number,
): CursorQueryResult<GatewayPayout> {
  return useCursorList(
    financeKeys.gateway.payouts({ ...filters, limit }),
    ({ cursor, limit: pageLimit, signal }) =>
      listGatewayPayouts(
        {
          ...filters,
          ...(cursor === undefined || cursor === null ? {} : { cursor }),
          ...(pageLimit === undefined ? {} : { limit: pageLimit }),
        },
        signal,
      ),
    { limit },
  );
}

// ── Reports ──────────────────────────────────────────────────────────────

export function useARAgingReport(query: ARAgingReportQuery, enabled = true) {
  return useQuery<ARAgingReport>({
    queryKey: financeKeys.reports.arAging({ ...query }),
    queryFn: ({ signal }) => getARAgingReport(query, signal),
    enabled,
  });
}

export function useAPAgingReport(query: APAgingReportQuery, enabled = true) {
  return useQuery<APAgingReport>({
    queryKey: financeKeys.reports.apAging({ ...query }),
    queryFn: ({ signal }) => getAPAgingReport(query, signal),
    enabled,
  });
}

// ── Reference lookups ────────────────────────────────────────────────────

export function usePostableAccounts(enabled = true) {
  return useQuery<Account[]>({
    queryKey: financeKeys.lookups.accounts(),
    queryFn: ({ signal }) => listPostableAccounts(signal),
    enabled,
  });
}

export function usePartyOptions(role: PartyRole, enabled = true) {
  return useQuery<PartySummary[]>({
    queryKey: financeKeys.lookups.parties(role),
    queryFn: ({ signal }) => listParties(role, signal),
    enabled,
  });
}

export function useFiscalPeriods(enabled = true) {
  return useQuery<FiscalPeriod[]>({
    queryKey: financeKeys.lookups.fiscalPeriods(),
    queryFn: ({ signal }) => listFiscalPeriods(signal),
    enabled,
  });
}

export function useFiscalYears(enabled = true) {
  return useQuery<FiscalYear[]>({
    queryKey: financeKeys.lookups.fiscalYears(),
    queryFn: ({ signal }) => listFiscalYears(signal),
    enabled,
  });
}

export function usePurchaseOrderOptions(supplierId: string | null, enabled = true) {
  return useQuery<PurchaseOrderSummary[]>({
    queryKey: financeKeys.lookups.purchaseOrders(supplierId ?? ""),
    queryFn: ({ signal }) => listPurchaseOrders(supplierId as string, signal),
    enabled: supplierId !== null && enabled,
  });
}

export function usePurchaseOrder(purchaseOrderId: string | null, enabled = true) {
  return useQuery<PurchaseOrder>({
    queryKey: financeKeys.lookups.purchaseOrder(purchaseOrderId ?? ""),
    queryFn: ({ signal }) => getPurchaseOrder(purchaseOrderId as string, signal),
    enabled: purchaseOrderId !== null && enabled,
  });
}

export function useGrnOptions(purchaseOrderId: string | null, enabled = true) {
  return useQuery<GrnSummary[]>({
    queryKey: financeKeys.lookups.grns(purchaseOrderId ?? ""),
    queryFn: ({ signal }) => listGrns(purchaseOrderId as string, signal),
    enabled: purchaseOrderId !== null && enabled,
  });
}

/**
 * Every GRN line behind one purchase order, keyed by purchase-order line, so a
 * capture line can name the exact receipt it invoices without the clerk
 * navigating receipts one by one.
 */
export function useGrnLinesForPurchaseOrder(purchaseOrderId: string | null, enabled = true) {
  const grnsQuery = useGrnOptions(purchaseOrderId, enabled);
  const grnIds = (grnsQuery.data ?? []).map((grn) => grn.id);
  return useQuery<Grn[]>({
    queryKey: financeKeys.lookups.grnLines(purchaseOrderId ?? "", grnIds),
    queryFn: ({ signal }) => Promise.all(grnIds.map((grnId) => getGrn(grnId, signal))),
    enabled: purchaseOrderId !== null && enabled && grnIds.length > 0,
  });
}
